import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import SidebarLinkGroup from "./SidebarLinkGroup";
import Logo from "../../images/logo/logo.svg";
import { useAuth } from "../../provider/AuthProvider.tsx";
import { useFlags } from "flagsmith/react";
import flagsmith from "flagsmith";

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (arg: boolean) => void;
  desktopSidebarOpen: boolean;
  setDesktopSidebarOpen: (arg: boolean) => void;
  className?: string;
}

const LINK_BASE_CLASSES =
  "text-white hover:bg-secondary/10 dark:hover:bg-secondary/20 group relative flex items-center gap-3 rounded-sm px-4 py-2 text-sm font-medium duration-300 ease-in-out border-l-4 border-transparent hover:border-accent/60";
const LINK_ACTIVE_CLASSES =
  "bg-secondary/15 dark:bg-secondary/20 border-l-4 border-secondary !text-white";
const SUBLINK_BASE_CLASSES =
  "text-white/80 group relative flex items-center gap-2.5 pl-2 text-[13px] rounded-sm font-medium duration-300 ease-in-out hover:text-white hover:bg-secondary/20";
const CHEVRON_CLASSES =
  "fa-solid absolute right-4 top-1/2 -translate-y-1/2 fill-current text-[11px]";

const Sidebar = ({
  sidebarOpen,
  setSidebarOpen,
  desktopSidebarOpen,
  setDesktopSidebarOpen,
  className = "",
}: SidebarProps) => {
  const location = useLocation();
  const { pathname } = location;
  const { userID } = useAuth();

  const flags = useFlags(["integrations"]);
  const showIntegrations = flags.integrations?.enabled === true;
  const isAdmin = userID && Number(userID) < 100;

  const trigger = useRef<HTMLButtonElement>(null);
  const sidebar = useRef<HTMLElement>(null);

  const storedSidebarExpanded = localStorage.getItem("sidebar-expanded");
  const [sidebarExpanded, setSidebarExpanded] = useState(
    storedSidebarExpanded === "true"
  );

  useEffect(() => {
    if (userID) {
      flagsmith.identify(userID);
      console.log("User ID:", userID);
      console.log("Feature Flags:", { integrations: flags.integrations });
    }
  }, [userID]);

  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!sidebar.current || !trigger.current) return;

      if (!sidebarOpen && !desktopSidebarOpen) return;

      if (
        sidebar.current.contains(target as Node) ||
        trigger.current.contains(target as Node)
      ) {
        return;
      }

      setSidebarOpen(false);
      setDesktopSidebarOpen(false);
    };

    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  }, [sidebarOpen, desktopSidebarOpen, setSidebarOpen, setDesktopSidebarOpen]);

  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if ((!sidebarOpen && !desktopSidebarOpen) || keyCode !== 27) return;
      setSidebarOpen(false);
      setDesktopSidebarOpen(false);
    };

    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, [sidebarOpen, desktopSidebarOpen, setSidebarOpen, setDesktopSidebarOpen]);

  useEffect(() => {
    localStorage.setItem("sidebar-expanded", sidebarExpanded.toString());
    const body = document.querySelector("body");
    if (body) {
      body.classList.toggle("sidebar-expanded", sidebarExpanded);
    }
  }, [sidebarExpanded]);

  const handleSidebarToggle = (
    e: React.MouseEvent,
    handleClick: () => void
  ) => {
    e.preventDefault();
    sidebarExpanded ? handleClick() : setSidebarExpanded(true);
  };

  return (
    <aside
      ref={sidebar}
      className={`dark:bg-box-dark bg-main-background-dark left-0 top-0 flex h-screen flex-col overflow-hidden transition-all duration-300 ease-in-out lg:static ${className}
    ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
    ${
      desktopSidebarOpen
        ? "lg:w-64 lg:translate-x-0"
        : "lg:w-0 lg:translate-x-0"
    }
    z-[9999]`}
    >
      <div className="min-w-64  flex items-center justify-between gap-2 px-5 pb-4 pt-5 lg:pt-6">
        <NavLink to="/">
          <img src={Logo} alt="Logo" className="max-w-[140px]" />
        </NavLink>

        <button
          ref={trigger}
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-controls="sidebar"
          aria-expanded={sidebarOpen}
          className="text-secondary hover:text-secondary-light transition-colors duration-200 lg:hidden"
        >
          <i className="fa-solid fa-arrow-left text-sm" />
        </button>
      </div>

      <div className="no-scrollbar min-w-64 flex flex-col overflow-y-auto duration-300 ease-linear">
        {/* Sidebar Menu */}
        <nav className="mt-4 px-4 py-3 lg:mt-5 lg:px-5">
          <div>
            <h3 className="mb-2.5 ml-4 flex items-center gap-2 text-[18px] font-bold tracking-wider text-white opacity-95">
              <span className="bg-accent/60 h-1.5 w-1.5 rounded-full"></span>
              МЕНЮ
            </h3>

            <ul className="mb-4 flex flex-col gap-1">
              {/* Policies Section */}
              <SidebarLinkGroup activeCondition={pathname === "/"}>
                {(handleClick, open) => (
                  <>
                    <NavLink
                      to="#"
                      className={`${LINK_BASE_CLASSES} ${
                        pathname.includes("policies") && LINK_ACTIVE_CLASSES
                      }`}
                      onClick={(e) => handleSidebarToggle(e, handleClick)}
                    >
                      <i className="fas fa-file-text w-4 text-center text-[13px]" />
                      <span>Полици</span>
                      <i
                        className={`${CHEVRON_CLASSES} ${
                          open ? "fa-chevron-up" : "fa-chevron-down"
                        }`}
                      />
                    </NavLink>

                    <div
                      className={`translate transform overflow-hidden ${
                        !open && "hidden"
                      }`}
                    >
                      <ul className="mb-3 mt-1 flex flex-col gap-1.5 pl-6">
                        <li>
                          <NavLink
                            to="/policies/new"
                            className={({ isActive }) =>
                              `${SUBLINK_BASE_CLASSES} ${
                                isActive && "!bg-secondary/15 !text-white"
                              }`
                            }
                          >
                            Работа с полици
                          </NavLink>
                        </li>
                        <li>
                          <NavLink
                            to="/policies/change"
                            className={({ isActive }) =>
                              `${SUBLINK_BASE_CLASSES} ${
                                isActive && "!bg-secondary/15 !text-white"
                              }`
                            }
                          >
                            Добавъци
                          </NavLink>
                        </li>
                        <li>
                          <NavLink
                            to="/policies/client"
                            className={({ isActive }) =>
                              `${SUBLINK_BASE_CLASSES} ${
                                isActive && "!bg-secondary/15 !text-white"
                              }`
                            }
                          >
                            Клиенти
                          </NavLink>
                        </li>
                        <li>
                          <NavLink
                            to="/policies/vehicle"
                            className={({ isActive }) =>
                              `${SUBLINK_BASE_CLASSES} ${
                                isActive && "!bg-secondary/15 !text-white"
                              }`
                            }
                          >
                            Данни за МПС
                          </NavLink>
                        </li>
                        <li>
                          <NavLink
                            to="/policies/new/policy"
                            className={({ isActive }) =>
                              `${SUBLINK_BASE_CLASSES} ${
                                isActive && "!bg-secondary/15 !text-white"
                              }`
                            }
                          >
                            Нови полици
                          </NavLink>
                        </li>
                        <li>
                          <NavLink
                            to="/policies/new/documents"
                            className={({ isActive }) =>
                              `${SUBLINK_BASE_CLASSES} ${
                                isActive && "!bg-secondary/15 !text-white"
                              }`
                            }
                          >
                            Нови документи
                          </NavLink>
                        </li>
                        <li>
                          <NavLink
                            to="/policies/ppp"
                            className={({ isActive }) =>
                              `${SUBLINK_BASE_CLASSES} ${
                                isActive && "!bg-secondary/15 !text-white"
                              }`
                            }
                          >
                            ППП на документи
                          </NavLink>
                        </li>
                      </ul>
                    </div>
                  </>
                )}
              </SidebarLinkGroup>

              {/* Integrations Section */}
              {showIntegrations && (
                <SidebarLinkGroup
                  activeCondition={pathname.includes("integrations")}
                >
                  {(handleClick, open) => (
                    <>
                      <NavLink
                        to="#"
                        className={`${LINK_BASE_CLASSES} ${
                          pathname.includes("integrations") &&
                          LINK_ACTIVE_CLASSES
                        }`}
                        onClick={(e) => handleSidebarToggle(e, handleClick)}
                      >
                        <i className="fas fa-plug w-4 text-center text-[13px]" />
                        <span>Интеграции</span>
                        <i
                          className={`${CHEVRON_CLASSES} ${
                            open ? "fa-chevron-up" : "fa-chevron-down"
                          }`}
                        />
                      </NavLink>

                      <div
                        className={`translate transform overflow-hidden ${
                          !open && "hidden"
                        }`}
                      >
                        <ul className="mt-1 flex flex-col gap-1.5 pl-6 pr-2">
                          <li>
                            <NavLink
                              to="/integrations/insurance-calculator"
                              className={({ isActive }) =>
                                `${SUBLINK_BASE_CLASSES} ${
                                  isActive && "!bg-secondary/15 !text-white"
                                }`
                              }
                            >
                              Калкулатор
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              to="/integrations/ozk"
                              className={({ isActive }) =>
                                `${SUBLINK_BASE_CLASSES} ${
                                  isActive && "!bg-secondary/15 !text-white"
                                }`
                              }
                            >
                              ОЗК
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              to="/integrations/dzi"
                              className={({ isActive }) =>
                                `${SUBLINK_BASE_CLASSES} ${
                                  isActive && "!bg-secondary/15 !text-white"
                                }`
                              }
                            >
                              ДЗИ
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              to="/integrations/generali"
                              className={({ isActive }) =>
                                `${SUBLINK_BASE_CLASSES} ${
                                  isActive && "!bg-secondary/15 !text-white"
                                }`
                              }
                            >
                              Generali
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              to="/integrations/levins"
                              className={({ isActive }) =>
                                `${SUBLINK_BASE_CLASSES} ${
                                  isActive && "!bg-secondary/15 !text-white"
                                }`
                              }
                            >
                              ЛевИнс
                            </NavLink>
                          </li>
                        </ul>
                      </div>
                    </>
                  )}
                </SidebarLinkGroup>
              )}

              {/* References Section */}
              <SidebarLinkGroup
                activeCondition={pathname.includes("references")}
              >
                {(handleClick, open) => (
                  <>
                    <NavLink
                      to="#"
                      className={`${LINK_BASE_CLASSES} ${
                        pathname.includes("references") && LINK_ACTIVE_CLASSES
                      }`}
                      onClick={(e) => handleSidebarToggle(e, handleClick)}
                    >
                      <i className="fas fa-info w-4 text-center text-[13px]" />
                      <span>Справки</span>
                      <i
                        className={`${CHEVRON_CLASSES} ${
                          open ? "fa-chevron-up" : "fa-chevron-down"
                        }`}
                      />
                    </NavLink>

                    <div
                      className={`translate transform overflow-hidden ${
                        !open && "hidden"
                      }`}
                    >
                      <ul className="mb-3 mt-1 flex flex-col gap-1.5 pl-6">
                        <li>
                          <NavLink
                            to="/references/expiring-policies"
                            className={({ isActive }) =>
                              `${SUBLINK_BASE_CLASSES} ${
                                isActive && "!bg-secondary/15 !text-white"
                              }`
                            }
                          >
                            Изтичащи полици
                          </NavLink>
                        </li>
                        <li>
                          <NavLink
                            to="/references/policy-installments"
                            className={({ isActive }) =>
                              `${SUBLINK_BASE_CLASSES} ${
                                isActive && "!bg-secondary/15 !text-white"
                              }`
                            }
                          >
                            Поредни вноски
                          </NavLink>
                        </li>
                        <li>
                          <NavLink
                            to="/references/document-search"
                            className={({ isActive }) =>
                              `${SUBLINK_BASE_CLASSES} ${
                                isActive && "!bg-secondary/15 !text-white"
                              }`
                            }
                          >
                            Търсене по номер на документ
                          </NavLink>
                        </li>
                        <li>
                          <NavLink
                            to="/references/empty-documents"
                            className={({ isActive }) =>
                              `${SUBLINK_BASE_CLASSES} ${
                                isActive && "!bg-secondary/15 !text-white"
                              }`
                            }
                          >
                            Празни документи
                          </NavLink>
                        </li>
                        <li>
                          <NavLink
                            to="/references/broker-report"
                            className={({ isActive }) =>
                              `${SUBLINK_BASE_CLASSES} ${
                                isActive && "!bg-secondary/15 !text-white"
                              }`
                            }
                          >
                            Дневник на Брокера
                          </NavLink>
                        </li>
                        <li>
                          <NavLink
                            to="/references/agent-report"
                            className={({ isActive }) =>
                              `${SUBLINK_BASE_CLASSES} ${
                                isActive && "!bg-secondary/15 !text-white"
                              }`
                            }
                          >
                            Отчет на Агент
                          </NavLink>
                        </li>
                        <li>
                          <NavLink
                            to="/references/daily-report"
                            className={({ isActive }) =>
                              `${SUBLINK_BASE_CLASSES} ${
                                isActive && "!bg-secondary/15 !text-white"
                              }`
                            }
                          >
                            Дневно приключване
                          </NavLink>
                        </li>
                        <li>
                          <NavLink
                            to="/references/awaiting-payment-report"
                            className={({ isActive }) =>
                              `${SUBLINK_BASE_CLASSES} ${
                                isActive && "!bg-secondary/15 !text-white"
                              }`
                            }
                          >
                            Изчакващи плащане
                          </NavLink>
                        </li>
                        {isAdmin && (
                          <li>
                            <NavLink
                              to="/references/reported-insurances"
                              className={({ isActive }) =>
                                `${SUBLINK_BASE_CLASSES} ${
                                  isActive && "!bg-secondary/15 !text-white"
                                }`
                              }
                            >
                              Отчетени полици в ЗК
                            </NavLink>
                          </li>
                        )}
                      </ul>
                    </div>
                  </>
                )}
              </SidebarLinkGroup>

              {/* Admin Sections */}
              {isAdmin && (
                <>
                  {/* System Section */}
                  <SidebarLinkGroup
                    activeCondition={pathname.includes("system")}
                  >
                    {(handleClick, open) => (
                      <>
                        <NavLink
                          to="#"
                          className={`${LINK_BASE_CLASSES} ${
                            pathname.includes("system") && LINK_ACTIVE_CLASSES
                          }`}
                          onClick={(e) => handleSidebarToggle(e, handleClick)}
                        >
                          <i className="fa-jelly-fill fa-regular fa-chart-line w-4 text-center text-[13px]"></i>
                          <span>Административни</span>
                          <i
                            className={`${CHEVRON_CLASSES} ${
                              open ? "fa-chevron-up" : "fa-chevron-down"
                            }`}
                          />
                        </NavLink>

                        <div
                          className={`translate transform overflow-hidden ${
                            !open && "hidden"
                          }`}
                        >
                          <ul className="mb-3 mt-1 flex flex-col gap-1.5 pl-6">
                            <li>
                              <NavLink
                                to="/system/kfn-report"
                                className={({ isActive }) =>
                                  `${SUBLINK_BASE_CLASSES} ${
                                    isActive && "!bg-secondary/15 !text-white"
                                  }`
                                }
                              >
                                Справка за КФН
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                to="/system/insurance-company-report"
                                className={({ isActive }) =>
                                  `${SUBLINK_BASE_CLASSES} ${
                                    isActive && "!bg-secondary/15 !text-white"
                                  }`
                                }
                              >
                                Отчет към ЗК
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                to="/system/activity-log"
                                className={({ isActive }) =>
                                  `${SUBLINK_BASE_CLASSES} ${
                                    isActive && "!bg-secondary/15 !text-white"
                                  }`
                                }
                              >
                                Движение на документи
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                to="/system/bank-report"
                                className={({ isActive }) =>
                                  `${SUBLINK_BASE_CLASSES} ${
                                    isActive && "!bg-secondary/15 !text-white"
                                  }`
                                }
                              >
                                Отчет по банка
                              </NavLink>
                            </li>

                            <li>
                              <NavLink
                                to="/system/pos-report"
                                className={({ isActive }) =>
                                  `${SUBLINK_BASE_CLASSES} ${
                                    isActive && "!bg-secondary/15 !text-white"
                                  }`
                                }
                              >
                                Отчет по POS
                              </NavLink>
                            </li>

                            <li>
                              <NavLink
                                to="/system/kfn-operations-report"
                                className={({ isActive }) =>
                                  `${SUBLINK_BASE_CLASSES} ${
                                    isActive && "!bg-secondary/15 !text-white"
                                  }`
                                }
                              >
                                Оперативни справки за КФН
                              </NavLink>
                            </li>
                          </ul>
                        </div>
                      </>
                    )}
                  </SidebarLinkGroup>

                  {/* Settings Section */}
                  <SidebarLinkGroup
                    activeCondition={pathname.includes("settings")}
                  >
                    {(handleClick, open) => (
                      <>
                        <NavLink
                          to="#"
                          className={`${LINK_BASE_CLASSES} ${
                            pathname.includes("settings") && LINK_ACTIVE_CLASSES
                          }`}
                          onClick={(e) => handleSidebarToggle(e, handleClick)}
                        >
                          <i className="fas fa-tools w-4 text-center text-[13px]" />
                          <span>Настройки</span>
                          <i
                            className={`${CHEVRON_CLASSES} ${
                              open ? "fa-chevron-up" : "fa-chevron-down"
                            }`}
                          />
                        </NavLink>

                        <div
                          className={`translate transform overflow-hidden ${
                            !open && "hidden"
                          }`}
                        >
                          <ul className="mb-3 mt-1 flex flex-col gap-1.5 pl-6">
                            <li>
                              <NavLink
                                to="/settings/insurance-companies"
                                className={({ isActive }) =>
                                  `${SUBLINK_BASE_CLASSES} ${
                                    isActive && "!bg-secondary/15 !text-white"
                                  }`
                                }
                              >
                                Застрахователни компании
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                to="/settings/consultants"
                                className={({ isActive }) =>
                                  `${SUBLINK_BASE_CLASSES} ${
                                    isActive && "!bg-secondary/15 !text-white"
                                  }`
                                }
                              >
                                Рекламни консултанти
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                to="/settings/policies"
                                className={({ isActive }) =>
                                  `${SUBLINK_BASE_CLASSES} ${
                                    isActive && "!bg-secondary/15 !text-white"
                                  }`
                                }
                              >
                                Видове полици
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                to="/settings/commissions"
                                className={({ isActive }) =>
                                  `${SUBLINK_BASE_CLASSES} ${
                                    isActive && "!bg-secondary/15 !text-white"
                                  }`
                                }
                              >
                                Комисионни
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                to="/settings/users"
                                className={({ isActive }) =>
                                  `${SUBLINK_BASE_CLASSES} ${
                                    isActive && "!bg-secondary/15 !text-white"
                                  }`
                                }
                              >
                                Потребители
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                to="/settings/offices"
                                className={({ isActive }) =>
                                  `${SUBLINK_BASE_CLASSES} ${
                                    isActive && "!bg-secondary/15 !text-white"
                                  }`
                                }
                              >
                                Офиси
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                to="/settings/offices-and-agents"
                                className={({ isActive }) =>
                                  `${SUBLINK_BASE_CLASSES} ${
                                    isActive && "!bg-secondary/15 !text-white"
                                  }`
                                }
                              >
                                Офиси и агенти
                              </NavLink>
                            </li>
                          </ul>
                        </div>
                      </>
                    )}
                  </SidebarLinkGroup>
                </>
              )}
            </ul>
          </div>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
